<template>
  <div>
    <label :for="id">{{ label }}</label>
    <div
      class="nt-input"
      :class="[{ 'nt-input--error': localError }, { 'nt-input--rounded': rounded }, getTheme]"
    >
      <input
        :id="id"
        ref="input"
        v-model.trim="selected"
        :name="id"
        type="text"
        :placeholder="placeholder"
        :label="label"
        maxlength="100"
        @change="changeForm"
        @keypress="keypress"
        @keydown.enter="enterClicked"
        @keydown="handleBackspace"
        @input="applyMask"
        @blur="validateField"
      >
      <button
        v-if="buttonSubmit"
        id="btn-submit"
        ref="submit"
        class="button button--default"
        @click.prevent="onSubmit"
      >
        {{ buttonSubmit }}
      </button>
    </div>
    <div
      v-if="localError"
      class="nt-form-error"
    >
      {{ localError }}
    </div>
    <div
      v-if="helperText"
      class="nt-form-text-helper"
    >
      {{ helperText }}
    </div>
  </div>
</template>

<script>
import { isCNPJ } from 'validation-br'
import { mapActions, mapState } from 'vuex'
import emitter from 'tiny-emitter/instance'
import utils from '../../../../utils/utils'

export default {
  name: 'nt-input',
  props: {
    id: {
      type: String,
      default: 'input'
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    validators: {
      type: Array,
      default: []
    },
    regex: {
      type: String,
      default: '(.*?)'
    },
    rounded: {
      type: Boolean,
      default: false
    },
    buttonSubmit: {
      type: String,
      default: ''
    },
    theme: {
      type: String,
      default: 'default'
    },
    mask: {
      type: String,
    },
    maskMode: {
      type: String,
      default: 'numeric'
    },
    helperText: {
      type: String,
      default: ''
    },
    viaCep: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    autocompleteFieldMapping: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selected: null,
      localError: null
    }
  },
  computed: {
    ...mapState('formStore', ['errors', 'formData']),
    getTheme() {
      return `nt-input--${this.theme}`
    }
  },
  watch: {
    error: {
      handler(val) {
        this.localError = val
      },
    },
    errors: {
      handler(values) {
        const { id } = this
        const error = values.find((value) => value[id]) || {}
        this.localError = error[id]
      },
      deep: true
    },
    formData: {
      handler(val) {
        if (!Object.prototype.hasOwnProperty.call(val, this.id)) return

        if (val[this.id] !== this.selected) {
          this.selected = val[this.id]

          if (this.selected && this.localError) {
            this.localError = null
            this.removeError(this.id)
          }
        }
      },
      deep: true
    }
  },
  created() {
    // Add to form initial values
    this.changeForm()
    // Add validators to form
    this.changeValidators({
      [this.id]: this.validators
    })
  },
  mounted() {
    if (this.buttonSubmit && window.innerWidth > 992) {
      this.$refs.input.style.paddingRight = `${this.$refs.submit.clientWidth + 20}px`
    }
  },
  methods: {
    ...mapActions('formStore', ['changeFormData', 'changeValidators', 'addError', 'removeError']),
    changeForm() {
      emitter.emit('changeForm', {
        [this.id]: this.selected
      })
    },
    keypress(event) {
      if (this.validators.includes('email')) return
      if (this.validators.includes('cnpj')) return
      if (this.regex && !event.key.match(this.regex)) {
        event.preventDefault()
      }
    },
    onSubmit() {
      emitter.emit('formSubmit')
    },
    enterClicked(event) {
      event.preventDefault()
      if (this.buttonSubmit) {
        this.changeForm()
        this.onSubmit()
      }
    },
    filterByRegex(value) {
      if (this.validators.includes('email') || this.validators.includes('cnpj')) return value
      if (!this.regex) return value

      return value.split('').filter((char) => char.match(this.regex)).join('')
    },
    applyMask(event) {
      const { value } = event.target

      if (!this.mask || this.mask.trim() === '') {
        this.selected = this.filterByRegex(value)
        return
      }

      if (this.maskMode === 'alphanumeric') {
        const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()

        let charIndex = 0
        let maskedValue = ''
        for (let i = 0; i < this.mask.length; i++) {
          const maskChar = this.mask[i]
          if (maskChar === 'X' || maskChar === '9') {
            maskedValue += cleanValue[charIndex] || ''
            charIndex++
          } else if (cleanValue[charIndex - 1] !== undefined) {
            maskedValue += maskChar
          }
        }

        this.selected = maskedValue
        this.changeForm()
        return
      }

      const numericValue = value.replace(/\D/g, '')

      let j = 0
      let maskedValue = ''
      for (let i = 0; i < this.mask.length; i++) {
        if (this.mask[i] === 'x') {
          maskedValue += numericValue[j] || ''
          j++
        } else if (numericValue[j - 1] !== undefined) {
          maskedValue += this.mask[i]
        }
      }

      this.selected = maskedValue

      if (this.viaCep) {
        const cleanCep = this.selected.replace(/\D/g, '')

        if (cleanCep.length === 8) {
          emitter.emit('fetchViaCep', {
            cep: cleanCep,
            mapping: this.autocompleteFieldMapping
          })
        }
      }

      this.changeForm()
    },
    handleBackspace(event) {
      if (!this.mask) {
        return
      }

      if (event.key === 'Backspace' && event.target.selectionStart > 0) {
        const { value } = event.target
        const { selectionStart } = event.target
        let maskPosition = 0
        for (let i = selectionStart; i > 0; i--) {
          if (this.mask[i] === 'x') {
            maskPosition = selectionStart - 1
          }
        }
        if (this.mask[maskPosition] !== 'x') {
          event.preventDefault(
            this.selected = value.substring(0, selectionStart - 1) + value.substring(selectionStart)
          )
        }
      }
    },
    validateField() {
      const isRequired = this.validators.includes('required')
      const isEmail = this.validators.includes('email')
      const isCnpj = this.validators.includes('cnpj')

      if (isRequired && !this.selected) {
        this.localError = this.error || 'Este campo é obrigatório'
        this.removeError(this.id)
        this.addError({ [this.id]: this.localError })
        return
      }

      if (isEmail && this.selected && !utils.isValidEmail(this.selected)) {
        this.localError = this.error || 'E-mail inválido'
        this.removeError(this.id)
        this.addError({ [this.id]: this.localError })
        return
      }

      if (isCnpj && this.selected && !isCNPJ(this.selected)) {
        this.localError = this.error || 'CNPJ inválido'
        this.removeError(this.id)
        this.addError({ [this.id]: this.localError })
        return
      }

      this.localError = null
      this.removeError(this.id)
    }
  }
}
</script>

<style lang="scss" scoped>
  .nt-input {
    &--small {
      input {
        padding: 10px;
        font-size: 1rem;
      }
    }
  }
</style>
